import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

export default function SalesTraining() {
  const canonical = 'https://earnmyedge.com/sales-training';
  const ld = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Sales Training Platform',
    description:
      'Earn My Edge is an AI-powered sales training platform offering daily drills, objection handling, and coaching across SPIN, Sandler, Challenger, MEDDIC, and more.',
    url: canonical,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://earnmyedge.com/' },
        { '@type': 'ListItem', position: 2, name: 'Sales Training', item: canonical },
      ],
    },
  };

  return (
    <main className="mx-auto max-w-4xl px-6 py-12">
      <Helmet>
        <title>Sales Training Platform | Earn My Edge</title>
        <meta name="description" content="AI-powered sales training with daily drills, objection handling, and framework-guided practice to help reps close more deals." />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="Sales Training Platform | Earn My Edge" />
        <meta property="og:description" content="AI-powered sales training with daily drills, objection handling, and framework-guided practice to help reps close more deals." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content="/assets/social-card.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Sales Training Platform | Earn My Edge" />
        <meta name="twitter:description" content="AI-powered sales training with daily drills, objection handling, and framework-guided practice to help reps close more deals." />
        <meta name="twitter:image" content="/assets/social-card.png" />
        <script type="application/ld+json">{JSON.stringify(ld)}</script>
      </Helmet>

      <header className="mb-8">
        <h1 className="text-3xl font-semibold">Sales Training That Actually Sticks</h1>
        <p className="mt-4 text-muted-foreground">
          PitchLab blends daily practice, AI coaching, and proven frameworks to build skills that translate into pipeline and revenue.
        </p>
      </header>

      <section className="space-y-6">
        <div>
          <h2 className="text-xl font-medium">Daily Drills & Coaching</h2>
          <p className="mt-2">
            Train in short, focused blocks with instant AI feedback across clarity, confidence, control, and close. Scores translate into XP and mastery progression.
          </p>
          <div className="mt-3">
            <Link to="/app/daily-drill" className="text-primary underline">Try a Daily Drill</Link>
          </div>
        </div>

        <div>
          <h2 className="text-xl font-medium">Objection Handling Dojo</h2>
          <p className="mt-2">
            Practice real-world objections with AI roleplay. Learn counter strategies and refine delivery with rubric-based scoring.
          </p>
          <div className="mt-3">
            <Link to="/app/objection-dojo" className="text-primary underline">Handle Objections Now</Link>
          </div>
        </div>

        <div>
          <h2 className="text-xl font-medium">Frameworks Covered</h2>
          <p className="mt-2">
            SPIN, Sandler, Challenger, MEDDIC, NEPQ, GAP, Straight Line, Value Selling — with guided lessons and scenario practice.
          </p>
          <div className="mt-3">
            <Link to="/app/mega-trainer" className="text-primary underline">Explore the Mega Trainer</Link>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t">
          <h2 className="text-2xl font-medium mb-6">Common Questions</h2>
          <div className="space-y-4">
            <div>
              <h3 className="font-medium">How often should I practice?</h3>
              <p className="mt-1 text-sm text-muted-foreground">Daily practice (10-15 minutes) builds muscle memory and confidence. Consistent reps drive lasting improvement.</p>
            </div>
            <div>
              <h3 className="font-medium">Can I customize drill difficulty?</h3>
              <p className="mt-1 text-sm text-muted-foreground">Yes. Select beginner, intermediate, or advanced drills based on your skill level and comfort.</p>
            </div>
            <div>
              <h3 className="font-medium">Do I need sales experience to start?</h3>
              <p className="mt-1 text-sm text-muted-foreground">No. Beginners can start with foundational drills and progress as they build confidence and competence.</p>
            </div>
          </div>
        </div>
      </section>

      <footer className="mt-10 border-t pt-6">
        <p className="font-medium">Related pages</p>
        <ul className="mt-2 list-disc pl-6">
          <li><Link to="/ai-sales-coach" className="underline">AI Sales Coach</Link></li>
          <li><Link to="/objection-handling-training" className="underline">Objection Handling Training</Link></li>
          <li><Link to="/sales-frameworks" className="underline">Sales Framework Guides</Link></li>
          <li><Link to="/sales-roleplay-scenarios" className="underline">Sales Roleplay Scenarios</Link></li>
        </ul>
      </footer>
    </main>
  );
}
